"use client";

import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { Hub } from "aws-amplify";
import { toast } from "sonner";
import { isAuthenticated } from "@/lib/auth";
import {
	getImagerySessionCookie,
	setImagerySessionCookie,
} from "@/lib/cookies";
import { getCognitoUserEmail } from "@/services/cognito";
import { imageryCheckIn } from "@/services/imagery-api";

export type ImageryLandingSessionStatus =
	| "idle"
	| "checking"
	| "guest"
	| "authenticated"
	| "error";

type ImageryLandingSessionContextValue = {
	status: ImageryLandingSessionStatus;
	/** On /imagery guest flow: whether imagery_email cookie is set */
	hasImagerySessionCookie: boolean;
};

const ImageryLandingSessionContext =
	createContext<ImageryLandingSessionContextValue | null>(null);

export function ImageryLandingSessionProvider({
	children,
}: {
	children: ReactNode;
}) {
	const pathname = usePathname();
	const [status, setStatus] = useState<ImageryLandingSessionStatus>("idle");
	const [hasImagerySessionCookie, setHasImagerySessionCookie] =
		useState(false);

	useEffect(() => {
		if (pathname !== "/imagery") {
			setStatus("idle");
			return;
		}

		let cancelled = false;

		const run = async () => {
			setStatus("checking");
			const authed = await isAuthenticated();
			if (cancelled) return;

			if (!authed) {
				setHasImagerySessionCookie(
					Boolean(getImagerySessionCookie()?.email)
				);
				setStatus("guest");
				return;
			}

			const email = await getCognitoUserEmail();
			if (cancelled) return;

			if (!email) {
				toast.error(
					"Could not read your account email. Try signing in again."
				);
				setStatus("error");
				return;
			}

			const existing = getImagerySessionCookie();
			if (
				existing?.email === email &&
				existing.enrichmentPublicEntityId
			) {
				setHasImagerySessionCookie(true);
				setStatus("authenticated");
				return;
			}

			try {
				const { enrichmentPublicEntityId } =
					await imageryCheckIn(email);
				if (cancelled) return;
				setImagerySessionCookie(email, enrichmentPublicEntityId);
				setHasImagerySessionCookie(true);
				setStatus("authenticated");
			} catch (e) {
				if (cancelled) return;
				console.error(e);
				toast.error(
					e instanceof Error
						? e.message
						: "Could not sync imagery session."
				);
				setStatus("error");
			}
		};

		void run();

		return () => {
			cancelled = true;
		};
	}, [pathname]);

	useEffect(() => {
		const listener = (data: { payload: { event: string } }) => {
			if (data.payload.event !== "signOut") return;
			if (pathname !== "/imagery") return;
			setHasImagerySessionCookie(
				Boolean(getImagerySessionCookie()?.email)
			);
			setStatus("guest");
		};
		Hub.listen("auth", listener);
		return () => {
			Hub.remove("auth", listener);
		};
	}, [pathname]);

	const value = useMemo(
		() => ({ status, hasImagerySessionCookie }),
		[status, hasImagerySessionCookie]
	);

	return (
		<ImageryLandingSessionContext.Provider value={value}>
			{children}
		</ImageryLandingSessionContext.Provider>
	);
}

export function useImageryLandingSession(): ImageryLandingSessionContextValue {
	const ctx = useContext(ImageryLandingSessionContext);
	if (!ctx) {
		throw new Error(
			"useImageryLandingSession must be used within ImageryLandingSessionProvider"
		);
	}
	return ctx;
}
